import { t } from '@glory/i18n';
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { type Area, FEEDBACK_IGNORE_ATTR } from './capture.ts';

const MIN_SIZE = 8;

/** Полноэкранный слой: пользователь обводит область мышью или пальцем. Esc — отмена. */
export function AreaSelect({
  onSelect,
  onCancel,
}: {
  readonly onSelect: (area: Area) => void;
  readonly onCancel: () => void;
}) {
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [area, setArea] = useState<Area | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onCancel]);

  const spanTo = (x: number, y: number): Area | null =>
    start
      ? {
          x: Math.min(start.x, x),
          y: Math.min(start.y, y),
          width: Math.abs(x - start.x),
          height: Math.abs(y - start.y),
        }
      : null;

  return createPortal(
    <motion.div
      {...{ [FEEDBACK_IGNORE_ATTR]: '' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[2147483000] cursor-crosshair touch-none select-none"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        setStart({ x: e.clientX, y: e.clientY });
        setArea({ x: e.clientX, y: e.clientY, width: 0, height: 0 });
      }}
      onPointerMove={(e) => {
        if (start) setArea(spanTo(e.clientX, e.clientY));
      }}
      onPointerUp={(e) => {
        const done = spanTo(e.clientX, e.clientY);
        setStart(null);
        if (done && done.width >= MIN_SIZE && done.height >= MIN_SIZE) onSelect(done);
        else setArea(null);
      }}
    >
      {area ? (
        // Затемнение вокруг рамки — огромной тенью, сама рамка остаётся прозрачной.
        <div
          className="pointer-events-none absolute rounded-sm border-2 border-white shadow-[0_0_0_200vmax_rgba(0,0,0,0.45)]"
          style={{ left: area.x, top: area.y, width: area.width, height: area.height }}
        />
      ) : (
        <div className="pointer-events-none absolute inset-0 bg-black/45" />
      )}
      <div className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 rounded-md bg-strong px-3 py-1.5 text-sm font-medium text-on-strong shadow-float">
        {t('feedback.selectHint')}
      </div>
    </motion.div>,
    document.body,
  );
}
